<template>
  <div>
    <h2 class="text-sm font-semibold text-gray-900 mb-1">Attribution by Report Date and Contract</h2>
    <p class="text-xs text-gray-400 mb-4">Patient panel and cost data from the selected CMS quarterly report. Cost columns reflect the rolling 12-month window ending at the report date.</p>

    <!-- Controls -->
    <div class="flex flex-wrap items-end gap-4 mb-5">
      <!-- Contract -->
      <div>
        <label class="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Contract</label>
        <select
          v-model="selectedContract"
          @change="onContractChange"
          class="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option v-for="c in contracts" :key="c.contract_id" :value="c.contract_id" class="sensitive" data-spoof-kind="contract">
            {{ c.contract_id }}
          </option>
        </select>
      </div>

      <!-- Report Date -->
      <div v-if="reportDates.length">
        <label class="block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">Report Date</label>
        <select
          v-model="selectedDate"
          @change="fetchPatients"
          class="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option class="sensitive" v-for="r in reportDates" :key="r.report_dt" :value="r.report_dt">
            {{ formatDate(r.report_dt) }} ({{ r.patient_count?.toLocaleString() }})
          </option>
        </select>
      </div>

      <div class="flex-1" />

      <!-- Summary -->
      <div v-if="!loading && patients.length" class="text-sm text-gray-500">
        <strong class="text-gray-900">{{ filteredPatients.length.toLocaleString() }}</strong>
        <span class="sensitive" v-if="filteredPatients.length !== patients.length"> of {{ patients.length.toLocaleString() }}</span>
        patients
      </div>

      <!-- Export -->
      <ExportMenu :disabled="!patients.length" :exports="[{ type: 'csv', action: exportCsv }]" />
    </div>

    <!-- Loading -->
    <div v-if="initialLoading || loading" class="py-16 text-center text-sm text-gray-400">Loading...</div>

    <!-- PCP Summary Table (default view) -->
    <template v-else-if="!selectedPcp && pcpSummary.length">
      <p class="text-xs text-gray-400 mb-2">Click a provider row to see their individual patients.</p>
      <div class="border border-gray-200 rounded-lg" style="max-height: 70vh; overflow: auto;">
        <table class="w-full text-left">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <th v-for="col in pcpColumns" :key="col.key"
                  class="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-gray-800"
                  :class="{ 'text-right': col.align === 'right' }"
                  @click="togglePcpSort(col.key)"
              >
                {{ col.label }}
                <span v-if="pcpSortKey === col.key" class="ml-0.5 text-[9px] sensitive">{{ pcpSortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedPcpSummary" :key="row.pcp"
                class="border-t border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                @click="selectedPcp = row.pcp"
            >
              <td class="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                <span class="inline-flex items-center gap-1 sensitive">
                  {{ row.pcp }}
                  <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-[8px] text-gray-400" />
                </span>
              </td>
              <td class="px-3 py-2 text-sm text-gray-900 text-right whitespace-nowrap font-medium sensitive">{{ row.patients.toLocaleString() }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-700">{{ formatCurrency(row.total_cost) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-700">{{ formatCurrency(row.ip_cost) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-700">{{ formatCurrency(row.snf_cost) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-600">{{ formatInt(row.ip_discharges) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-600">{{ formatInt(row.ed_visits) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-600 sensitive">{{ formatInt(row.pcp_visits) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono text-gray-600">{{ formatPY(row.person_years) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Patient Detail Table (drilldown view) -->
    <template v-else-if="selectedPcp">
      <div class="mb-3 flex items-center gap-4">
        <button
          @click="selectedPcp = null; search = ''"
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <span class="text-[10px]">←</span> All PCPs
        </button>
        <span class="text-sm font-medium text-gray-900 sensitive">{{ selectedPcp }}</span>
        <span class="text-sm text-gray-400">·</span>
        <span class="text-sm text-gray-500">{{ filteredPatients.length }} patients</span>

        <div class="flex-1" />

        <input
          v-model="search" data-demo-search
          type="text"
          placeholder="Search by name or ID..."
          class="sensitive w-64 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>

      <div v-if="filteredPatients.length" class="border border-gray-200 rounded-lg" style="max-height: 70vh; overflow: auto;">
        <table class="w-full text-left">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <th v-for="col in patientColumns" :key="col.key"
                  class="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-gray-800"
                  :class="{ 'text-right': col.align === 'right' }"
                  @click="toggleSort(col.key)"
              >
                {{ col.label }}
                <span v-if="sortKey === col.key" class="ml-0.5 text-[9px]">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in sortedPatients" :key="patientId(p)"
                class="border-t border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <td class="px-3 py-2 text-sm font-medium text-gray-900 whitespace-nowrap sensitive">{{ p.bene_last_name }}, {{ p.bene_first_name }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap font-mono sensitive">{{ p.bene_mbi_id || p.other_patient_identifier || '—' }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap sensitive">{{ p.bene_age ?? '—' }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap sensitive">{{ sexLabel(p.bene_sex_cd) }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap sensitive">{{ p.entity_name || '—' }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap sensitive">{{ p.facility_name || '—' }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono" :class="costClass(p.total_cost)">{{ formatCurrency(p.total_cost) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono" :class="costClass(p.ip_cost)">{{ formatCurrency(p.ip_cost) }}</td>
              <td class="px-3 py-2 text-sm text-right whitespace-nowrap font-mono" :class="costClass(p.snf_cost)">{{ formatCurrency(p.snf_cost) }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 text-right whitespace-nowrap">{{ formatInt(p.hospital_discharges) }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 text-right whitespace-nowrap">{{ formatInt(p.ed_visits) }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 text-right whitespace-nowrap sensitive">{{ formatInt(p.pcp_visits) }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 text-right whitespace-nowrap">{{ formatPY(p.person_years) }}</td>
              <td class="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                <span v-if="p.full_dual" class="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 rounded">Dual</span>
                <span v-if="p.esrd" class="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-rose-50 text-rose-700 rounded ml-1">ESRD</span>
                <span v-if="p.disabled" class="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded ml-1">Disabled</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="py-8 text-center text-sm text-gray-400">No patients match filters.</div>
    </template>

    <!-- Empty -->
    <div v-else-if="!loading" class="py-16 text-center text-sm text-gray-400 sensitive">
      {{ availableReports.length ? 'No patients for this report.' : 'No attribution reports available.' }}
    </div>
  </div>
</template>

<script>
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { blockSensitiveEgress } from '@/utils/demoEgress'
import demoProtectedFields from '@/mixins/demoProtectedFields'

export default {
  name: 'AttributionPatientReport',
  components: { ExportMenu },
  mixins: [demoProtectedFields],
  // The patient name/ID search identifies real patients. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['search'],
  props: {
    entityId: { type: Number, default: null },
    careSetting: { type: String, default: '' },
  },
  data() {
    return {
      availableReports: [],
      selectedContract: null,
      selectedDate: null,
      selectedPcp: null,
      patients: [],
      loading: false,
      initialLoading: true,
      search: '',
      sortKey: 'bene_last_name',
      sortDir: 'asc',
      pcpSortKey: 'patients',
      pcpSortDir: 'desc',
    };
  },
  computed: {
    contracts() {
      const map = new Map();
      this.availableReports.forEach(r => {
        if (!map.has(r.contract_id)) map.set(r.contract_id, r);
      });
      return Array.from(map.values());
    },
    reportDates() {
      if (!this.selectedContract) return [];
      return this.availableReports
        .filter(r => r.contract_id === this.selectedContract)
        .sort((a, b) => new Date(b.report_dt) - new Date(a.report_dt));
    },
    pcpColumns() {
      return [
        { key: 'pcp', label: 'PCP' },
        { key: 'patients', label: 'Patients', align: 'right' },
        { key: 'total_cost', label: 'Total Cost', align: 'right' },
        { key: 'ip_cost', label: 'IP Cost', align: 'right' },
        { key: 'snf_cost', label: 'SNF Cost', align: 'right' },
        { key: 'ip_discharges', label: 'IP Disch', align: 'right' },
        { key: 'ed_visits', label: 'ED', align: 'right' },
        { key: 'pcp_visits', label: 'PCP Visits', align: 'right' },
        { key: 'person_years', label: 'Person-Years', align: 'right' },
      ];
    },
    patientColumns() {
      return [
        { key: 'bene_last_name', label: 'Name' },
        { key: 'bene_mbi_id', label: 'Patient ID' },
        { key: 'bene_age', label: 'Age', align: 'right' },
        { key: 'bene_sex_cd', label: 'Sex' },
        { key: 'entity_name', label: 'Practice' },
        { key: 'facility_name', label: 'Facility' },
        { key: 'total_cost', label: 'Total Cost', align: 'right' },
        { key: 'ip_cost', label: 'IP', align: 'right' },
        { key: 'snf_cost', label: 'SNF', align: 'right' },
        { key: 'hospital_discharges', label: 'IP Disch', align: 'right' },
        { key: 'ed_visits', label: 'ED', align: 'right' },
        { key: 'pcp_visits', label: 'PCP Visits', align: 'right' },
        { key: 'person_years', label: 'Person-Years', align: 'right' },
        { key: 'flags', label: 'Flags' },
      ];
    },
    pcpSummary() {
      const map = {};
      this.patients.forEach(p => {
        const name = p.pcp_name || '(No PCP)';
        if (!map[name]) {
          map[name] = { pcp: name, patients: 0, total_cost: 0, ip_cost: 0, snf_cost: 0, ip_discharges: 0, ed_visits: 0, pcp_visits: 0, person_years: 0 };
        }
        const r = map[name];
        r.patients++;
        r.total_cost += p.total_cost || 0;
        r.ip_cost += p.ip_cost || 0;
        r.snf_cost += p.snf_cost || 0;
        r.ip_discharges += p.hospital_discharges || 0;
        r.ed_visits += p.ed_visits || 0;
        r.pcp_visits += p.pcp_visits || 0;
        r.person_years += p.person_years || 0;
      });
      return Object.values(map);
    },
    sortedPcpSummary() {
      const rows = [...this.pcpSummary];
      const key = this.pcpSortKey;
      const dir = this.pcpSortDir === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const va = a[key] ?? '', vb = b[key] ?? '';
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
      return rows;
    },
    filteredPatients() {
      let list = this.patients;
      if (this.selectedPcp) {
        const pcp = this.selectedPcp;
        list = list.filter(p => (p.pcp_name || '(No PCP)') === pcp);
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(p =>
          (p.bene_last_name || '').toLowerCase().includes(q) ||
          (p.bene_first_name || '').toLowerCase().includes(q) ||
          (p.bene_mbi_id || '').toLowerCase().includes(q) ||
          (p.other_patient_identifier || '').toLowerCase().includes(q)
        );
      }
      return list;
    },
    sortedPatients() {
      const rows = [...this.filteredPatients];
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const numericKeys = ['total_cost', 'ip_cost', 'snf_cost', 'hospital_discharges', 'ed_visits', 'pcp_visits', 'bene_age', 'person_years'];
      rows.sort((a, b) => {
        let va = a[key], vb = b[key];
        if (va == null) va = numericKeys.includes(key) ? -Infinity : '';
        if (vb == null) vb = numericKeys.includes(key) ? -Infinity : '';
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
      return rows;
    },
  },
  methods: {
    async fetchReports() {
      this.initialLoading = true;
      const payload = {};
      if (this.entityId) payload.entity_id = this.entityId;
      if (this.careSetting) payload.care_setting = this.careSetting;
      const { data } = await this.$axios.post('/api/portfolio_management/compare-reports/', payload);
      this.availableReports = data.available_reports || [];
      this.initialLoading = false;
      if (this.contracts.length) {
        this.selectedContract = this.contracts[0].contract_id;
        this.onContractChange();
      }
    },
    onContractChange() {
      if (this.reportDates.length) {
        this.selectedDate = this.reportDates[0].report_dt;
        this.fetchPatients();
      } else {
        this.selectedDate = null;
        this.patients = [];
      }
    },
    async fetchPatients() {
      if (!this.selectedContract || !this.selectedDate) return;
      this.loading = true;
      this.search = '';
      this.selectedPcp = null;
      // Now sourced from the unified claims-pivot engine (metric 925,
      // ATTRIBUTION_PATIENT_REPORT_FACT) in detail mode — same patient-grain
      // roster rows for the selected contract + report date.
      const payload = {
        metric_id: 925,
        mode: 'detail',
        contract_id: this.selectedContract,
        report_dt: this.selectedDate,
        limit: 50000,
      };
      if (this.entityId) payload.entity_id = this.entityId;
      if (this.careSetting) payload.care_setting = this.careSetting;
      const { data } = await this.$axios.post('/api/claim-explorer/pivot/', payload);
      this.patients = data || [];
      this.loading = false;
    },
    togglePcpSort(key) {
      if (this.pcpSortKey === key) {
        this.pcpSortDir = this.pcpSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.pcpSortKey = key;
        this.pcpSortDir = key === 'pcp' ? 'asc' : 'desc';
      }
    },
    toggleSort(key) {
      if (key === 'flags') return;
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = ['total_cost', 'ip_cost', 'snf_cost', 'hospital_discharges', 'ed_visits', 'pcp_visits', 'bene_age', 'person_years'].includes(key) ? 'desc' : 'asc';
      }
    },
    patientId(p) {
      return p.bene_mbi_id || p.other_patient_identifier || Math.random();
    },
    sexLabel(cd) {
      if (cd === '1' || cd === 1) return 'M';
      if (cd === '2' || cd === 2) return 'F';
      return cd || '—';
    },
    formatCurrency(val) {
      if (val == null) return '—';
      return '$' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    formatInt(val) {
      if (val == null) return '—';
      return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    formatPY(val) {
      if (val == null) return '—';
      return Number(val).toFixed(2);
    },
    formatDate(dt) {
      if (!dt) return '';
      const s = String(dt).split('T')[0];
      const parts = s.split('-');
      if (parts.length !== 3) return s;
      const [y, m, d] = parts;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
    },
    costClass(val) {
      if (val == null) return 'text-gray-400';
      return val > 50000 ? 'text-rose-600' : 'text-gray-700';
    },
    exportCsv() {
      if (blockSensitiveEgress('export')) return;
      let headers, rows, suffix
      if (!this.selectedPcp) {
        // PCP summary view
        headers = ['PCP', 'Patients', 'Total Cost', 'IP Cost', 'SNF Cost',
          'IP Discharges', 'ED Visits', 'PCP Visits', 'Person-Years']
        rows = this.sortedPcpSummary.map(r => [
          r.pcp, r.patients, r.total_cost ?? '', r.ip_cost ?? '', r.snf_cost ?? '',
          r.ip_discharges ?? '', r.ed_visits ?? '', r.pcp_visits ?? '', r.person_years ?? '',
        ])
        suffix = 'pcp_summary'
      } else {
        // Patient detail view
        headers = ['Last Name', 'First Name', 'Patient ID', 'Age', 'Sex', 'PCP', 'Practice',
          'Total Cost', 'IP Cost', 'SNF Cost', 'IP Discharges', 'ED Visits', 'PCP Visits',
          'Person-Years', 'Full Dual', 'ESRD', 'Disabled', 'Death Date']
        rows = this.filteredPatients.map(p => [
          p.bene_last_name, p.bene_first_name,
          p.bene_mbi_id || p.other_patient_identifier || '',
          p.bene_age ?? '', this.sexLabel(p.bene_sex_cd),
          p.pcp_name || '', p.entity_name || '',
          p.total_cost ?? '', p.ip_cost ?? '', p.snf_cost ?? '',
          p.hospital_discharges ?? '', p.ed_visits ?? '', p.pcp_visits ?? '',
          p.person_years ?? '',
          p.full_dual ? 'Y' : '', p.esrd ? 'Y' : '', p.disabled ? 'Y' : '',
          p.bene_death_dt ? p.bene_death_dt.split('T')[0] : '',
        ])
        suffix = 'patients'
      }
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attribution_${this.selectedContract}_${this.selectedDate}_${suffix}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
  watch: {
    careSetting() { this.fetch(); },
    entityId() {
      this.fetchReports();
    },
  },
  mounted() {
    this.fetchReports();
  },
};
</script>
